import React, { useState, useMemo } from 'react';
import './DataTable.css';
import FilterCell from './FilterCell';

// פונקציה להמרת תאריך לפורמט dd/MM/yyyy
const formatDate = (dateValue) => {
  if (!dateValue) return '';
  
  try {
    let date;
    
    // אם זה כבר Date object
    if (dateValue instanceof Date) {
      date = dateValue;
    }
    // אם זה string - נסה לפרסר
    else if (typeof dateValue === 'string') {
      // אם זה בפורמט SQL (YYYY-MM-DD) - פרסר ידנית
      const sqlDateMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (sqlDateMatch) {
        const [, year, month, day] = sqlDateMatch;
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        // נסה לפרסר תאריך ISO או פורמטים אחרים
        date = new Date(dateValue);
      }
      
      // בדוק אם התאריך תקין
      if (isNaN(date.getTime())) {
        return dateValue; // אם לא הצלחנו לפרסר - החזר את הערך המקורי
      }
    }
    // אם זה מספר (timestamp)
    else if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    }
    else {
      return String(dateValue);
    }
    
    // המר ל-dd/MM/yyyy
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    // אם יש שגיאה - החזר את הערך המקורי
    return String(dateValue);
  }
};

// columns: [{label, field, type, width, wrap, ...}]
// data: [{...}]
// contracts: [{id, name}]
export default function DataTable({ columns, data, defaultPageSize = 20, contracts = [], onContractChange, customToolbarButtons }) {
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ field: null, direction: null });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [selectedContract, setSelectedContract] = useState(contracts[0]?.id || '');
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);

  // חיפוש כללי
  const filteredData = useMemo(() => {
    let filtered = data;
    // חיפוש כללי
    if (search) {
      filtered = filtered.filter(row =>
        columns.some(col => String(row[col.field] || '').toLowerCase().includes(search.toLowerCase()))
      );
    }
    // סינון עמודות (input + select)
    Object.entries(filters).forEach(([field, filterObj]) => {
      if (!filterObj) return;
      if (filterObj.select && Array.isArray(filterObj.select) && filterObj.select.length > 0) {
        filtered = filtered.filter(row => filterObj.select.includes(String(row[field] || '')));
      }
      if (filterObj.input) {
        filtered = filtered.filter(row => String(row[field] || '').toLowerCase().includes(filterObj.input.toLowerCase()));
      }
    });
    // מיון
    if (sort.field) {
      filtered = [...filtered].sort((a, b) => {
        const av = a[sort.field];
        const bv = b[sort.field];
        if (av === bv) return 0;
        if (sort.direction === 'asc') return av > bv ? 1 : -1;
        return av < bv ? 1 : -1;
      });
    }
    return filtered;
  }, [data, search, filters, sort, columns]);

  // דפדוף
  const pageCount = Math.ceil(filteredData.length / pageSize);
  const pagedData = filteredData.slice((page - 1) * pageSize, page * pageSize);

  // שינוי מיון
  const handleSort = (field) => {
    setSort(prev => {
      if (prev.field === field) {
        if (prev.direction === 'asc') return { field, direction: 'desc' };
        if (prev.direction === 'desc') return { field: null, direction: null };
      }
      return { field, direction: 'asc' };
    });
  };

  // שינוי סינון
  const handleFilter = (field, type, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: { ...prev[field], [type]: value }
    }));
    setPage(1);
  };

  // שינוי חוזה
  const handleContractChange = (e) => {
    setSelectedContract(e.target.value);
    if (onContractChange) onContractChange(e.target.value);
  };

  // יצוא לאקסל (פשוט)
  const exportToExcel = () => {
    // יצירת CSV עם BOM לתמיכה בעברית ב-Excel
    const BOM = '\uFEFF'; // UTF-8 BOM
    
    const csv = [
      columns.map(col => col.label).join(','),
      ...filteredData.map(row => columns.map(col => {
        const value = row[col.field] || '';
        // החלף גרשיים כפולים (אם יש) כדי למנוע בעיות ב-CSV
        const escapedValue = String(value).replace(/"/g, '""');
        return `"${escapedValue}"`;
      }).join(','))
    ].join('\n');
    
    // הוסף BOM בתחילת הקובץ
    const csvWithBOM = BOM + csv;
    
    // השתמש ב-UTF-8 encoding
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'report.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // הדפסה
  const printTable = () => {
    window.print();
  };

  // שליחה במייל (פשוט - פותח מייל חדש)
  const sendEmail = () => {
    window.open('mailto:?subject=דוח&body=מצורף דוח');
  };

  return (
    <div className="datatable-container">
      {/* שורת כלים: בחירת חוזה, חיפוש, כפתורים */}
      <div className="datatable-toolbar">
        {contracts.length > 0 && (
          <select value={selectedContract} onChange={handleContractChange} className="datatable-select datatable-contract-select-inline">
            {contracts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <input
          type="text"
          placeholder="חיפוש חופשי..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          className="datatable-search"
          style={{ maxWidth: '50%' }}
        />
        {customToolbarButtons && customToolbarButtons}
        <button onClick={exportToExcel} className="datatable-btn datatable-btn-excel">יצא לאקסל</button>
        <button onClick={printTable} className="datatable-btn datatable-btn-print">הדפס</button>
        {/* <button onClick={sendEmail} className="datatable-btn datatable-btn-mail">שלח במייל</button> */}
      </div>
      {/* טבלה */}
      <div className="datatable-table-wrapper">
        <table className="datatable-table" style={{ fontSize: '0.96em' }}>
          <thead>
            <tr>
              {columns.map(col => (
                <th 
                  key={col.field} 
                  style={{ position: 'relative', cursor: 'pointer' }}
                  onDoubleClick={() => handleSort(col.field)}
                  title="לחיצה כפולה למיון"
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {col.label}
                    {sort.field === col.field && (
                      <span style={{ fontSize: '0.9em', color: '#6d4c2f' }}>
                        {sort.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                    <FilterCell
                      column={col}
                      data={data}
                      value={filters[col.field]}
                      onChange={(type, value) => handleFilter(col.field, type, value)}
                      icon="filter"
                    />
                  </span>
                </th>
              ))}
            </tr>           
          </thead>
          <tbody>
            {pagedData.length === 0 ? (
              <tr><td colSpan={columns.length} style={{ textAlign: 'center' }}>אין נתונים</td></tr>
            ) : (
              pagedData.map((row, i) => (
                <tr
                  key={i}
                  className={selectedRowIndex === i ? 'datatable-row-selected' : ''}
                >
                  {columns.map((col, j) => (
                    <td
                      key={col.field}
                      style={{ maxWidth: col.width, whiteSpace: col.wrap ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      className="datatable-td"
                      onClick={() => { if (j <= 2) setSelectedRowIndex(selectedRowIndex === i ? null : i); console.log('gg'); }}
                    >
                      {col.type === 'link' && row[col.field] ? (
                        <a href={row[col.field]} target="_blank" rel="noopener noreferrer">{row[col.field]}</a>
                      ) : col.type === 'sum' ? (
                        Number(row[col.field]).toLocaleString()
                      ) : col.type === 'date' ? (
                        formatDate(row[col.field])
                      ) : (
                        row[col.field]
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* דפדוף */}
      <div className="datatable-pagination" style={{ justifyContent: 'flex-start', direction: 'ltr' }}>
        <button onClick={() => setPage(1)} disabled={page === 1} className="datatable-btn datatable-btn-page">{'<<'}</button>
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="datatable-btn datatable-btn-page">{'<'}</button>
        <span className="datatable-pagination-info">עמוד {page} מתוך {pageCount} | מספר שורות {filteredData.length}</span>
        <button onClick={() => setPage(p => Math.min(pageCount, p + 1))} disabled={page === pageCount} className="datatable-btn datatable-btn-page">{'>'}</button>
        <button onClick={() => setPage(pageCount)} disabled={page === pageCount} className="datatable-btn datatable-btn-page">{'>>'}</button>
        <div className="datatable-page-size" style={{ marginRight: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
          <span>הצג</span>
          <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} className="datatable-select">
            {[10, 20, 30, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span>בעמוד</span>
        </div>
      </div>
    </div>
  );
} 